import type { Milestone, ProjectTask, TaskDependencyEdge, DependencyEdgeType } from '../types';
import { getDependencyEdges } from './taskDependencies';
import { stripTime } from './taskSchedule';

function normalizeMilestoneDepType(raw: unknown): DependencyEdgeType {
  return raw === 'SS' || raw === 'FS' || raw === 'FF' || raw === 'SF' ? raw : 'FS';
}

/** Synthetic id for milestones in the FS graph (matches `data-gantt-task-id` on Gantt). */
export const MILESTONE_BRIDGE_PREFIX = 'milestone:';

export function milestoneBridgeId(milestoneDocId: string): string {
  return `${MILESTONE_BRIDGE_PREFIX}${milestoneDocId}`;
}

export function isMilestoneBridgeId(id: string): boolean {
  return id.startsWith(MILESTONE_BRIDGE_PREFIX);
}

export function parseMilestoneBridgeId(id: string): string {
  return id.slice(MILESTONE_BRIDGE_PREFIX.length);
}

export function getMilestoneDependencyEdges(m: Milestone): TaskDependencyEdge[] {
  if (m.dependencyEdges && m.dependencyEdges.length > 0) {
    return m.dependencyEdges.map((e) => ({
      predecessorId: e.predecessorId,
      type: normalizeMilestoneDepType(e.type),
      lagBusinessDays: Math.max(-500, Math.min(500, Math.floor(Number(e.lagBusinessDays) || 0))),
    }));
  }
  return [];
}

/**
 * Single-day pseudo-task for FS scheduling (milestone = point-in-time at end of day).
 */
export function milestoneToPseudoTask(m: Milestone): ProjectTask {
  const d = stripTime(new Date(m.targetDate));
  const now = new Date();
  const edges = getMilestoneDependencyEdges(m);
  return {
    id: milestoneBridgeId(m.id!),
    projectId: m.projectId,
    title: m.title,
    description: m.description || '',
    department: 'design',
    assignedTo: [],
    status: m.status === 'completed' ? 'completed' : 'pending',
    priority: 'medium',
    startDate: d,
    endDate: d,
    dependencyEdges: edges.length ? edges : undefined,
    dependencies: edges.map((e) => e.predecessorId),
    progress: m.status === 'completed' ? 100 : 0,
    position: 0,
    notes: 'Milestone',
    scheduleLocked: m.scheduleLocked === true,
    lockedAt: m.lockedAt,
    lockedBy: m.lockedBy,
    createdAt: m.createdAt || now,
    updatedAt: m.updatedAt || now,
  };
}
